import { canDecodeImageFile } from 'utils/can-decode-image-file.ts';

const HEIC_EXTENSIONS = /\.(heic|heif)$/i;
const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']);
const HEIC_BRANDS = ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1', 'msf1'];

export class ImagePreparationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImagePreparationError';
  }
}

export async function prepareImageFile(file: File): Promise<File> {
  if (await canDecodeImageFile(file)) {
    return file;
  }

  if (!(await looksLikeHeic(file))) {
    throw new ImagePreparationError(
      '画像を読み込めませんでした。JPEG、PNG、GIF、WebP、HEIC形式の画像を選択してください。',
    );
  }

  try {
    const { decode } = await import('@discourse/heic');
    const imageData = await decode(await file.arrayBuffer());
    const converted = await imageDataToJpeg(imageData);
    const convertedFile = new File([converted], jpegFileName(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });

    if (!(await canDecodeImageFile(convertedFile))) {
      throw new Error('converted image decode failed');
    }
    return convertedFile;
  } catch {
    throw new ImagePreparationError(
      'HEIC画像を変換できませんでした。別の画像を選ぶか、JPEGとして書き出してから再度お試しください。',
    );
  }
}

async function imageDataToJpeg(imageData: ImageData): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('canvas is unavailable');
  context.putImageData(imageData, 0, 0);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('JPEG conversion failed'))),
      'image/jpeg',
      0.9,
    );
  });
}

async function looksLikeHeic(file: File): Promise<boolean> {
  if (HEIC_MIME_TYPES.has(file.type.toLowerCase()) || HEIC_EXTENSIONS.test(file.name)) {
    return true;
  }

  try {
    const bytes = new Uint8Array(await file.slice(0, 64).arrayBuffer());
    const header = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
    return header.slice(4, 8) === 'ftyp' && HEIC_BRANDS.some((brand) => header.includes(brand));
  } catch {
    return false;
  }
}

function jpegFileName(fileName: string): string {
  const base = fileName.replace(/\.[^.]+$/, '') || 'image';
  return `${base}.jpg`;
}
